import React, { useEffect, useState } from "react";
import Layout from "../../../components/layouts/Layout";
import Section from "../../../components/base/Section";
import { getUser, setPage, useHub } from "../../../core/Global";
import AdminDashboardModel from "../models/AdminDashboardModel";
import KpiCard from "./widgets/KpiCard";
import RevenueChart from "./widgets/RevenueChart";
import JobsStatusDonut from "./widgets/JobsStatusDonut";
import CallsVolumeChart from "./widgets/CallsVolumeChart";
import TodayScheduleWidget from "./widgets/TodayScheduleWidget";
import TopTechniciansWidget from "./widgets/TopTechniciansWidget";
import ActivityTimelineWidget from "./widgets/ActivityTimelineWidget";
import RecentJobsWidget from "./widgets/RecentJobsWidget";
import OverdueInvoicesWidget from "./widgets/OverdueInvoicesWidget";
import Icon from "../../../components/base/Icon";
import "./AdminDashboard.css";

function AdminDashboard() {

    const hub = useHub();
    const user = getUser();
    const [model, modelSet] = useState<AdminDashboardModel>();
    const [loading, loadingSet] = useState<boolean>(true);

    const fetch = () => {
        loadingSet(true);
        hub.Get("/dashboard/AdminDashboard/summary", res => {
            if (res?.data) modelSet(res.data);
            loadingSet(false);
        });
    };

    useEffect(() => {
        setPage("Dashboard");
        fetch();
    }, []);

    const k = model?.kpis;
    const greeting = `Welcome back${user?.firstName ? ", " + user.firstName : ""}`;

    return (
        <Layout wide>
            <Section fluid className="py-6 admin-dashboard">

                <div className="d-flex flex-wrap align-items-center justify-content-between mb-6 px-2">
                    <div>
                        <h1 className="fw-bolder text-gray-900 mb-1" style={{ fontSize: "1.5rem" }}>{greeting}</h1>
                        <div className="text-gray-500 fs-7">Here's what's happening with your business today</div>
                    </div>
                    <button className="btn btn-sm btn-light-primary mt-2 mt-md-0" onClick={fetch} disabled={loading}>
                        <Icon icon="arrows-rotate" type="solid" size={12} className={"me-1 " + (loading ? "fa-spin" : "")} />
                        Refresh
                    </button>
                </div>

                <div className="row g-4 mb-4">
                    <div className="col-6 col-md-4 col-xl-2">
                        <KpiCard icon="briefcase" color="primary" label="Jobs Today" value={k?.jobsToday.value ?? 0} trend={k?.jobsToday} />
                    </div>
                    <div className="col-6 col-md-4 col-xl-2">
                        <KpiCard icon="dollar-sign" color="success" label="Revenue (mo)" value={"$" + (k?.revenueThisMonth.value.toLocaleString() ?? 0)} trend={k?.revenueThisMonth} />
                    </div>
                    <div className="col-6 col-md-4 col-xl-2">
                        <KpiCard icon="wrench" color="info" label="Active Techs" value={`${k?.activeTechnicians ?? 0}/${k?.totalTechnicians ?? 0}`} />
                    </div>
                    <div className="col-6 col-md-4 col-xl-2">
                        <KpiCard icon="file-invoice-dollar" color="warning" label="Open Invoices" value={k?.openInvoices ?? 0} secondary={`$${(k?.openInvoicesAmount ?? 0).toLocaleString()}`} />
                    </div>
                    <div className="col-6 col-md-4 col-xl-2">
                        <KpiCard icon="phone" color="primary" label="Calls Today" value={k?.callsToday.value ?? 0} trend={k?.callsToday} secondary={`${k?.inboundCallsToday ?? 0} in · ${k?.outboundCallsToday ?? 0} out`} />
                    </div>
                    <div className="col-6 col-md-4 col-xl-2">
                        <KpiCard icon="user-group" color="info" label="New Customers" value={k?.newCustomersThisMonth.value ?? 0} trend={k?.newCustomersThisMonth} />
                    </div>
                </div>

                <div className="row g-4 mb-4">
                    <div className="col-12 col-xl-8">
                        <RevenueChart model={model?.revenueChart} />
                    </div>
                    <div className="col-12 col-xl-4">
                        <JobsStatusDonut model={model?.jobsStatusDonut} />
                    </div>
                </div>

                <div className="row g-4 mb-4">
                    <div className="col-12 col-xl-8">
                        <CallsVolumeChart model={model?.callsVolumeChart} />
                    </div>
                    <div className="col-12 col-xl-4">
                        <ActivityTimelineWidget items={model?.recentActivities} />
                    </div>
                </div>

                <div className="row g-4 mb-4">
                    <div className="col-12 col-xl-7">
                        <TodayScheduleWidget items={model?.todaySchedule} />
                    </div>
                    <div className="col-12 col-xl-5">
                        <TopTechniciansWidget items={model?.topTechnicians} />
                    </div>
                </div>

                <div className="row g-4 mb-4">
                    <div className="col-12 col-xl-8">
                        <RecentJobsWidget items={model?.recentJobs} />
                    </div>
                    <div className="col-12 col-xl-4">
                        <OverdueInvoicesWidget items={model?.overdueInvoices} />
                    </div>
                </div>

            </Section>
        </Layout>
    );
}

export default AdminDashboard;
